import type { ReactNode } from "react";

/** デザインのアイコン（線のアイコン、24px グリッド）。すべて装飾として読み上げない */
function Icon({
  size = 20,
  strokeWidth = 1.7,
  children,
}: {
  size?: number;
  strokeWidth?: number;
  children: ReactNode;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

type Props = { size?: number; strokeWidth?: number };

export const GridIcon = (p: Props) => (
  <Icon {...p}>
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
  </Icon>
);

export const UserIcon = (p: Props) => (
  <Icon {...p}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
  </Icon>
);

export const ShieldIcon = (p: Props) => (
  <Icon {...p}>
    <path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3z" />
    <path d="M9 12l2 2 4-4" />
  </Icon>
);

export const HistoryIcon = (p: Props) => (
  <Icon {...p}>
    <path d="M3 12a9 9 0 1 0 3-6.7" />
    <path d="M3 4v4h4" />
    <path d="M12 7v5l3 2" />
  </Icon>
);

export const LogoutIcon = (p: Props) => (
  <Icon strokeWidth={1.8} {...p}>
    <path d="M15 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4" />
    <path d="M10 17l5-5-5-5" />
    <path d="M15 12H4" />
  </Icon>
);

export const ExternalIcon = (p: Props) => (
  <Icon strokeWidth={2} {...p}>
    <path d="M14 4h6v6" />
    <path d="M20 4l-9 9" />
    <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
  </Icon>
);

export const UploadIcon = (p: Props) => (
  <Icon strokeWidth={1.8} {...p}>
    <path d="M12 16V4" />
    <path d="M7 9l5-5 5 5" />
    <path d="M4 20h16" />
  </Icon>
);

export const CheckCircleIcon = (p: Props) => (
  <Icon strokeWidth={2} {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8 12.5l2.8 2.8L16 9.8" />
  </Icon>
);

export const CheckIcon = (p: Props) => (
  <Icon strokeWidth={2} {...p}>
    <path d="M5 12l5 5 9-10" />
  </Icon>
);

export const InfoIcon = (p: Props) => (
  <Icon strokeWidth={1.8} {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <path d="M12 8h.01" />
  </Icon>
);

export const WarningIcon = (p: Props) => (
  <Icon strokeWidth={1.8} {...p}>
    <path d="M12 3l9 16H3l9-16z" />
    <path d="M12 10v4" />
    <path d="M12 17h.01" />
  </Icon>
);

export const LockIcon = (p: Props) => (
  <Icon {...p}>
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </Icon>
);

export const KeyIcon = (p: Props) => (
  <Icon {...p}>
    <circle cx="8" cy="15" r="4" />
    <path d="M11 12l9-9" />
    <path d="M17 6l3 3" />
    <path d="M15 8l2 2" />
  </Icon>
);

export const PhoneIcon = (p: Props) => (
  <Icon {...p}>
    <rect x="7" y="2" width="10" height="20" rx="2" />
    <path d="M11 18h2" />
  </Icon>
);

export const MessageIcon = (p: Props) => (
  <Icon {...p}>
    <path d="M4 5h16v11H8l-4 4V5z" />
    <path d="M8 10h8" />
  </Icon>
);

export const LaptopIcon = (p: Props) => (
  <Icon {...p}>
    <rect x="3" y="5" width="18" height="11" rx="2" />
    <path d="M2 19h20" />
  </Icon>
);

export const PlusIcon = (p: Props) => (
  <Icon strokeWidth={2.2} {...p}>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </Icon>
);

export const CrossCircleIcon = (p: Props) => (
  <Icon strokeWidth={2} {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9 9l6 6" />
    <path d="M15 9l-6 6" />
  </Icon>
);

/** フッターの外部リンクのアイコン（ログインテンプレートと同じ） */
export const CorporateLinkIcon = () => (
  <svg width="10" height="9" viewBox="0 0 10 9" fill="none" aria-hidden="true" focusable="false">
    <path d="M6.70022 0.5H0.5V6.69999H6.70022V0.5Z" stroke="currentColor" />
    <path d="M1.7998 8.5H9.00002" stroke="currentColor" />
    <path d="M8.60034 1.80029V8.10028" stroke="currentColor" />
  </svg>
);
